import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import styles from "../../assets/styles/blog/BlogList.module.css";

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const PAGE_API = `${ENV_API_BASE_URL}/api/blog-page`;
const POSTS_API = `${ENV_API_BASE_URL}/api/blog-details`;

const DEFAULT_PAGE_CONTENT = {
  topLabel: "Wisdom, Insights & Inspiration",
  title: "Yoga Blog - Rishikesh Yogkulam®",
  breadcrumbLabel: "Home / Blog",
  sectionTitle: "Latest Articles & Yoga Insights",
  description:
    "Explore our collection of articles covering yoga philosophy, teacher training tips, pranayama techniques, and transformative experiences from our yoga community in Rishikesh.",
};

const resolveImageSrc = (value) => {
  if (!value || typeof value !== "string") return "";
  if (/^https?:\/\//.test(value)) return value;
  if (value.startsWith("/uploads/")) return `${ENV_API_BASE_URL}${value}`;
  return `${ENV_API_BASE_URL}/uploads/${value}`;
};

const stripHtml = (value = "") => String(value).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const buildExcerpt = (post) => {
  const source = post.heroSubtitle || post.introQuote || post.introText || post.whatDescription || "";
  const cleaned = stripHtml(source);
  if (cleaned.length <= 150) return cleaned;
  return `${cleaned.slice(0, 147).trim()}...`;
};

const estimateReadTime = (post) => {
  const text = stripHtml(
    [
      post.introQuote,
      post.introText,
      post.whatParagraph,
      post.whatDescription,
      post.stepparagraph,
      post.benefitparagraph,
      post.whyDescription,
      post.precautionsText,
      post.conclusion,
      post.faq1A,
      post.faq2A,
      post.faq3A,
      post.faq4A,
    ].join(" ")
  );

  const words = text ? text.split(/\s+/).length : 0;
  return `${Math.max(1, Math.ceil(words / 200))} min read`;
};

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const normalizePost = (post) => ({
  ...post,
  image: resolveImageSrc(post.BlogMainImage || post.heroImage),
  link: `/blog/${post.slug}`,
  title: post.heroTitle || "Untitled Blog Post",
  excerpt: buildExcerpt(post),
  date: formatDate(post.updatedAt || post.createdAt),
  readTime: estimateReadTime(post),
  category: post.category || "Uncategorized",
});

const BlogList = () => {
  const [activeCategory, setActiveCategory] = useState("All Posts");
  const [visibleCount, setVisibleCount] = useState(6);
  const [pageContent, setPageContent] = useState(DEFAULT_PAGE_CONTENT);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [pageRes, postsRes] = await Promise.all([
          axios.get(PAGE_API),
          axios.get(POSTS_API),
        ]);

        if (pageRes.data?.data) {
          setPageContent({
            ...DEFAULT_PAGE_CONTENT,
            ...pageRes.data.data,
          });
        }

        const nextPosts = Array.isArray(postsRes.data?.data)
          ? postsRes.data.data.map(normalizePost)
          : [];
        setPosts(nextPosts);
      } catch (error) {
        console.error("Failed to load blog page:", error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const categories = useMemo(() => {
    const dynamicCategories = Array.from(
      new Set(posts.map((post) => post.category).filter(Boolean))
    );
    return ["All Posts", ...dynamicCategories];
  }, [posts]);

  const filteredBlogs = useMemo(() => {
    if (activeCategory === "All Posts") return posts;
    return posts.filter((blog) => blog.category === activeCategory);
  }, [activeCategory, posts]);

  return (
    <section className={styles.wrapper}>
      <div className={styles.heroSection}>
        <div className={styles.heroContent}>
          <span className={styles.topLabel}>{pageContent.topLabel}</span>
          <h1 className={styles.pageTitle}>{pageContent.title}</h1>
          <div className={styles.breadcrumb}>{pageContent.breadcrumbLabel}</div>
        </div>
      </div>

      <div className={styles.contentSection}>
        <h2 className={styles.sectionTitle}>{pageContent.sectionTitle}</h2>
        <p className={styles.description}>{pageContent.description}</p>

        <div className={styles.filterTabs}>
          {categories.map((cat) => (
            <button
              key={cat}
              className={`${styles.filterTab} ${activeCategory === cat ? styles.activeTab : ""}`}
              onClick={() => {
                setActiveCategory(cat);
                setVisibleCount(6);
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-5 text-muted">Loading blog posts...</div>
        ) : filteredBlogs.length === 0 ? (
          <div className="text-center py-5 text-muted">No blog posts found.</div>
        ) : (
          <>
            <div className={styles.blogGrid}>
              {filteredBlogs.slice(0, visibleCount).map((blog) => (
                <article key={blog._id} className={styles.blogCard}>
                  <div className={styles.imageWrapper}>
                    {blog.image ? <img src={blog.image} alt={blog.title} /> : null}
                    <span className={styles.categoryBadge}>{blog.category}</span>
                    <div className={styles.imageOverlay}></div>
                  </div>

                  <div className={styles.cardContent}>
                    <div className={styles.metaInfo}>
                      <span className={styles.date}>{blog.date}</span>
                      <span className={styles.readTime}>{blog.readTime}</span>
                    </div>

                    <h3 className={styles.cardTitle}>{blog.title}</h3>
                    <p className={styles.cardExcerpt}>{blog.excerpt}</p>

                    <Link to={blog.link} className={styles.readMore}>
                      Read Full Article
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            {visibleCount < filteredBlogs.length && (
              <div className={styles.viewMoreWrapper}>
                <button
                  className={styles.viewMoreBtn}
                  onClick={() => setVisibleCount((count) => count + 3)}
                >
                  Load More Articles
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default BlogList;
